import { Button, Col, DatePicker, Input, Row, Segmented, Select, Space } from "antd";
import dayjs from 'dayjs';
import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { SearchSvg } from "utils/svgs";
import { checkPermission } from "utils/utils";
import styles from "./index.module.scss";
import { SearchAndFilterProps } from "./types";
const { RangePicker } = DatePicker;

// For search filter and paginate
const SearchAndFilter: FC<SearchAndFilterProps> = (props) => {
  // Init
  const {
    performSearchHandler,
    searchValue,
    title,
    setSearch,
    allUsers,
    setSelectedUser,
    selectedUser,
    selectedDate,
    setSelectedDate,
    totalRecords,
    medicineQcCount,
    qcTitle,
    qcEntity,
    setQcEntity,
    canViewMedicineQc,
    canViewComboQc
  } = props;

  const entityOptions = useMemo(() => {
    const options: { label: string; value: 'medicine' | 'combo' }[] = [];
    if (canViewMedicineQc) {
      options.push({ label: 'Medicines', value: 'medicine' });
    }
    if (canViewComboQc) {
      options.push({ label: 'Combos', value: 'combo' });
    }
    return options;
  }, [canViewMedicineQc, canViewComboQc]);

  const userPermissions = useSelector(
    (state: any) => state?.userProfile?.data
  );

  const isMedicineQcViewAll = checkPermission(userPermissions?.permissions, {
    permissionName: 'QualityCheck',
    permission: ['viewAll'],
  });

  const isComboQcViewAll = checkPermission(userPermissions?.permissions, {
    permissionName: 'ComboQualityCheck',
    permission: ['viewAll'],
  });

  const canViewAllUsers =
    userPermissions?.isAdmin ||
    (qcEntity === 'combo' ? isComboQcViewAll : isMedicineQcViewAll);

  const activeUserOptions = useMemo(
    () =>
      (allUsers || [])
        .filter((item: any) => item?.status !== false)
        .map((item: any) => ({
          label: item?.name,
          value: item?.userId,
        })),
    [allUsers]
  );

  const inactiveUserOptions = useMemo(
    () =>
      (allUsers || [])
        .filter((item: any) => item?.status === false)
        .map((item: any) => ({
          label: item?.name,
          value: item?.userId,
        })),
    [allUsers]
  );
 
  // JSX
  return (
    <div className={styles["search-filter"]}>
      <div className={styles["search-filter__wrapper"]}>
        <Row justify={"space-between"}>
          <Col>
            <Space size={16} align="center">
              <div className={styles["search-filter__wrapper--title"]}>
                {title}
                <span className={styles["search-filter__wrapper--title--count"]}>({totalRecords})</span>
              </div>
              {entityOptions.length > 1 && qcEntity && setQcEntity && (
                <Segmented
                  options={entityOptions}
                  value={qcEntity}
                  onChange={(value) =>
                    setQcEntity(value as 'medicine' | 'combo')
                  }
                />
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              {(!userPermissions?.isAdmin && qcEntity === 'medicine') &&
                <div className={styles["search-filter__wrapper--sub-title"]}>
                  {qcTitle}
                  <span className={styles["search-filter__wrapper--title--count"]}>({medicineQcCount})</span>
                </div>
              }
              <Button
                onClick={() => {
                  setSearch('');
                  if (canViewAllUsers) {
                    setSelectedUser(undefined);
                  }
                  setSelectedDate(null);
                }}
              >
                Clear all
              </Button>


              <Input
                className={styles["search-filter__search"]}
                placeholder="Search by Product Name"
                prefix={<SearchSvg />}
                onChange={performSearchHandler}
                value={searchValue}
                size="large"
                allowClear
              />

              {canViewAllUsers &&
                <Select
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={selectedUser}
                  placeholder="Select User"
                  size="large"
                  style={{ width: 200 }}
                  onChange={(value) => setSelectedUser(value)}
                  allowClear
                  options={
                    [
                      ...(activeUserOptions.length
                        ? [{ label: 'Active', options: activeUserOptions }]
                        : []),
                      ...(inactiveUserOptions.length
                        ? [{ label: 'Inactive', options: inactiveUserOptions }]
                        : []),
                    ]
                  }
                />
              }

              <RangePicker
                picker="date"
                format="DD/MM/YYYY"
                style={{ width: 120, height: 40 }}
                allowClear
                onChange={(value) => {
                  if (!value || !value[0] || !value[1]) {
                    setSelectedDate(null);
                  } else {
                    setSelectedDate([
                      dayjs(value[0]).startOf("day"),
                      dayjs(value[1]).endOf("day")
                    ]);
                  }
                }}
                value={selectedDate}
              />
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SearchAndFilter;
